import type { HTMLAttributes, ReactNode, ThHTMLAttributes } from 'react';
import { cn } from './cn.js';

/**
 * Tabela para o painel de administração.
 *
 * O guia avisa contra `clamp()` agressivo em UI densa — aqui os tamanhos são
 * fixos e pequenos de propósito, para o scroll não sofrer com milhares de
 * linhas.
 */
export function Table({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableElement>): ReactNode {
  return (
    <div className="overflow-x-auto rounded-mc-card border border-mc-line">
      <table
        className={cn('w-full border-collapse text-left text-sm', className)}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

export function Th({
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement>): ReactNode {
  return (
    <th
      scope="col"
      className={cn(
        'mc-mono whitespace-nowrap border-b border-mc-line bg-mc-surface-table-head px-4 py-3 font-normal',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableCellElement>): ReactNode {
  return (
    <td
      className={cn('border-b border-mc-line px-4 py-3 text-mc-ink2', className)}
      {...rest}
    >
      {children}
    </td>
  );
}

export function Tr({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>): ReactNode {
  return (
    <tr
      className={cn('transition-colors hover:bg-mc-surface-hover/50', className)}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 rounded-mc-card border border-dashed border-mc-line2 px-6 py-16 text-center">
      <p className="text-mc-card-title text-mc-ink">{title}</p>
      {description ? (
        <p className="max-w-md text-sm text-mc-ink2">{description}</p>
      ) : null}
      {action}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone = 'accent',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'accent' | 'neutral';
}): ReactNode {
  return (
    <div className="rounded-mc-card border border-mc-line2 bg-mc-surface-card p-5">
      <p className="mc-mono">{label}</p>
      <p
        className={cn(
          'mt-2 font-mc-display text-3xl font-semibold tabular-nums',
          tone === 'accent' ? 'text-mc-accent' : 'text-mc-ink',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-mc-ink3">{hint}</p> : null}
    </div>
  );
}

export function ProgressBar({
  value,
  max = 100,
  label,
}: {
  value: number;
  max?: number;
  label?: string;
}): ReactNode {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <div className="flex justify-between text-xs text-mc-ink3">
          <span>{label}</span>
          <span className="tabular-nums">{Math.round(pct)}%</span>
        </div>
      ) : null}
      <div
        className="h-1.5 overflow-hidden rounded-full bg-mc-surface-track"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-mc-accent to-mc-accent2 shadow-mc-glow-sm transition-[width] duration-500 ease-mc-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
